import React from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'katex/dist/katex.min.css';
import 'mathlive/static.css';
import 'mathlive';

const TextAreaInput = ({
    change,
    className = '',
    cols = 1,
    disabled,
    helpText,
    label,
    maxlength,
    name,
    onBlur,
    onFocus,
    placeholder,
    required,
    rows,
    value = '',
    inputRef,
    richText = false,
    withOutsideClick = true,
    onOutsideClick,
    operatorPanelIsView,
    ...inputProps
}) => {
    const quillRef = React.useRef(null);
    const textAreaRef = React.useRef(null);

    const [showSymbolPicker, setShowSymbolPicker] = React.useState(false);
    const [showMathSymbolPicker, setShowMathSymbolPicker] = React.useState(false);
    const toolbarId = `toolbar-${Math.random().toString(36).substr(2, 9)}`;

    const modules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'align': [] }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
            ],
            handlers: {
                formula: null,
                symbol: null,
            }
        },
        clipboard: {
            matchVisual: false,
        },
    };

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'list',
        'align', 'size',
        'math-field',
    ];

    const symbolsValue = [
        'Α','α','Β','β','Γ','γ','Δ','δ','Ε','ε','Ζ','ζ','Η','η','Θ','θ','Ι','ι','Κ','κ','Λ','λ','Μ','μ','Ν','ν','Ξ','ξ','Ο','ο','Π','π','Ρ','ρ','Σ','σ','Τ','τ','Υ','υ','Φ','φ','Χ','χ','Ψ','ψ','Ω','ω',
        'Ⅰ','Ⅱ','Ⅲ','Ⅳ','Ⅴ','Ⅵ','Ⅶ','Ⅷ','Ⅸ','Ⅹ','Ⅺ','Ⅻ','ⅰ','ⅱ','ⅲ','ⅳ','ⅴ','ⅵ','ⅶ','ⅷ','ⅸ','ⅹ','ⅺ','ⅻ',
        '°', '℃', '℉', '′', '″', '∠',
    ];
    const mathSymbols = [
        "∀", "∁", "∂", "∃", "∄", "∅", "∆", "∇", "∈", "∉", "∊", "∋", "∌", "∍", "∎",
        "∏", "∐", "∑", "−", "∓", "∔", "∕", "∖", "∗", "∘", "∙", "√", "∛", "∜", "∝",
        "∞", "∟", "∠", "∡", "∢", "∣", "∤", "∥", "∦", "∧", "∨", "∩", "∪", "∫", "∬",
        "∭", "∮", "∯", "∰", "∱", "∲", "∳", "∴", "∵", "∶", "∷", "∸", "∹", "∺", "∻",
        "∼", "∽", "∾", "∿", "≀", "≁", "≂", "≃", "≄", "≅", "≆", "≇", "≈", "≉", "≊",
        "≋", "≌", "≍", "≎", "≏", "≐", "≑", "≒", "≓", "≔", "≕", "≖", "≗", "≘", "≙",
        "≚", "≛", "≜", "≝", "≞", "≟", "≠", "≡", "≢", "≣", "≤", "≥", "≦", "≧", "≨",
        "≩", "≪", "≫", "≬", "≭", "≮", "≯", "≰", "≱", "≲", "≳", "≴", "≵", "≶", "≷",
        "≸", "≹", "≺", "≻", "≼", "≽", "≾", "≿", "⊀", "⊁", "⊂", "⊃", "⊄", "⊅", "⊆",
        "⊇", "⊈", "⊉", "⊊", "⊋"
    ];

    React.useEffect(() => {
        let customRef = null;
        let handleGlobalClick = null;

        if (richText && inputRef && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const root = editor?.root;

            if (root) {
                const listeners = {};

                const handleClick = (e) => {
                    inputProps?.onClick?.(e);

                    if (listeners['click']) {
                        listeners['click'](e);
                    }
                };

                root.addEventListener('click', handleClick);

                customRef = {
                    dom: root,
                    editor,
                    get value() {
                        return editor.root.innerHTML || '';
                    },
                    addEventListener: (event, callback) => {
                        root.addEventListener(event, callback);
                        listeners[event] = callback;
                    },
                    removeEventListener: (event, callback) => {
                        root.removeEventListener(event, callback);
                        delete listeners[event];
                    },
                    focus: () => root.focus(),
                };

                const handleSelectionChange = (range, oldRange) => {
                    if (range === null && oldRange !== null) {
                        onBlur?.();
                    } else if (range !== null && oldRange === null) {
                        onFocus?.();
                    }
                };
                editor.on('selection-change', handleSelectionChange);

                inputRef.current = customRef;

                handleGlobalClick = (e) => {
                    const suggestionBox = document.querySelector('.suggestion-box');

                    if (
                        root &&
                        !root.contains(e.target) &&
                        (!suggestionBox || !suggestionBox.contains(e.target)) &&
                        withOutsideClick
                    ) {
                        console.log('onOutsideClick');
                        onOutsideClick?.();
                    }
                };

                document.addEventListener('mousedown', handleGlobalClick);

                return () => {
                    editor.off('selection-change', handleSelectionChange);
                    Object.entries(listeners).forEach(([event, callback]) => {
                        if (event === 'click') {
                            root.removeEventListener(event, inputProps.onClick);
                        } else {
                            root.removeEventListener(event, callback);
                        }
                    });
                };
            }
        }

        if (!richText && inputRef && textAreaRef.current) {
            inputRef.current = textAreaRef.current;
        } else if (richText && inputRef && customRef) {
            inputRef.current = customRef;
        }
    }, [richText, inputRef, onBlur, onFocus, onOutsideClick]);

    const handleClickBtnMath = () => {
        setShowMathSymbolPicker(prev => !prev);
        setShowSymbolPicker(false);
    }

    const handleClickBtnSymbol = () => {
        setShowSymbolPicker(prev => !prev);
        setShowMathSymbolPicker(false);
    }

    React.useEffect(() => {
        if (richText && quillRef.current) {
            const editor = quillRef.current.getEditor();

            if (editor) {
                const toolbarEl = document.querySelector(`#${toolbarId}`);
                const button = toolbarEl.querySelector('.ql-symbols');
                const buttonMathSymbol = toolbarEl.querySelector('.ql-math-symbols');

                if (button) {
                    button.innerHTML = 'Ω';
                    button.addEventListener('click', handleClickBtnSymbol);
                }
                if (buttonMathSymbol) {
                    buttonMathSymbol.innerHTML = '∑';
                    buttonMathSymbol.addEventListener('click', handleClickBtnMath);
                }

                return () => {
                    if (button) {
                        button.removeEventListener('click', handleClickBtnSymbol);
                    }
                    if (buttonMathSymbol) {
                        buttonMathSymbol.removeEventListener('click', handleClickBtnMath);
                    }
                }
            }
        }
    }, [richText, quillRef.current]);

    const convertPlainTextToHTML = (text) => {
        if (!text || text.trim() === '') {
            return '';
        }

        const isEmptyQuillHTML = text.trim() === '<p><br></p>' || text.trim() === '<p></p>' || text.trim() === '<br>';
        if (isEmptyQuillHTML) {
            return '';
        }

        const hasHTMLTags = /<[^>]+>/g.test(text);

        if (hasHTMLTags && (text.includes('<p>') || text.includes('<div>') || text.includes('<span>'))) {
            return text;
        }

        const escapeHTML = (str) => {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        };
        
        const escapedText = escapeHTML(text);

        return `<p>${escapedText}</p>`;
    };

    const quillValue = React.useMemo(() => {
        if (!richText || !value) {
            return value || '';
        }
        const html = convertPlainTextToHTML(value);
        console.log('Quill value conversion:', { name, originalValue: value?.substring(0, 50), htmlValue: html?.substring(0, 100) });
        return html;
    }, [value, richText, name]);

    const prevValueRef = React.useRef(value);
    const isInternalChangeRef = React.useRef(false);
    const quillInitializedRef = React.useRef(false);

    React.useEffect(() => {
        if (richText && quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                if (!quillInitializedRef.current) {
                    quillInitializedRef.current = true;
                }
                const currentContent = editor.root.innerHTML || '';
                const htmlValue = quillValue || '';

                const isEmptyContent = (content) => {
                    return !content || content === '' || content === '<p><br></p>' || content === '<p></p>' || content.trim() === '<p><br></p>';
                };
                
                const isCurrentEmpty = isEmptyContent(currentContent);
                const isValueEmpty = isEmptyContent(htmlValue);

                if (isCurrentEmpty && !isValueEmpty) {
                    const updateQuill = () => {
                        try {
                            // Check current content again before updating
                            const currentCheck = editor.root.innerHTML || '';
                            if (isEmptyContent(currentCheck) && !isEmptyContent(htmlValue)) {
                                editor.clipboard.dangerouslyPasteHTML(htmlValue, 'silent');

                                const length = editor.getLength();
                                editor.setSelection(length - 1, 0, 'silent');
                                
                                prevValueRef.current = htmlValue;

                                setTimeout(() => {
                                    const afterUpdate = editor.root.innerHTML || '';
                                    const updateWorked = !isEmptyContent(afterUpdate);
                                    console.log('Quill initial content set:', { 
                                        name, 
                                        originalValue: value?.substring(0, 50), 
                                        htmlValue: htmlValue.substring(0, 100),
                                        beforeContent: currentCheck,
                                        afterContent: afterUpdate.substring(0, 100),
                                        updateWorked
                                    });
                                    if (!updateWorked && isEmptyContent(afterUpdate)) {
                                        console.warn('Quill update failed, trying direct HTML setting');
                                        editor.root.innerHTML = htmlValue;
                                        editor.update('silent');
                                    } else if (updateWorked) {
                                        editor.update('silent');
                                    }
                                }, 50);
                            }
                        } catch (error) {
                            console.warn('Error setting initial Quill content:', error);
                        }
                    };

                    updateQuill();
                    setTimeout(updateQuill, 50);
                    setTimeout(updateQuill, 150);
                    setTimeout(updateQuill, 300);
                }
            }
        }
    }, [richText, quillValue, value, name]);
    React.useEffect(() => {
        if (richText && quillRef.current && quillInitializedRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                const timeoutId = setTimeout(() => {
                    const currentContent = editor.root.innerHTML || '';
                    const htmlValue = quillValue || '';
                    const prevValue = prevValueRef.current || '';
                    
                    const isEmptyContent = (content) => {
                        return !content || content === '' || content === '<p><br></p>' || content === '<p></p>' || content.trim() === '<p><br></p>';
                    };

                    if (!isInternalChangeRef.current && 
                        htmlValue !== prevValue && 
                        htmlValue !== currentContent && 
                        !isEmptyContent(htmlValue)) {
                        
                        const isCurrentEmpty = isEmptyContent(currentContent);

                        if (isCurrentEmpty || (htmlValue.length > 20 && currentContent.length < 10)) {
                            try {
                                editor.clipboard.dangerouslyPasteHTML(htmlValue, 'silent');

                                const length = editor.getLength();
                                editor.setSelection(length - 1, 0, 'silent');
                                
                                prevValueRef.current = htmlValue;
                                
                                setTimeout(() => {
                                    const afterUpdate = editor.root.innerHTML || '';
                                    const isEmptyAfter = isEmptyContent(afterUpdate);
                                    console.log('Quill force updated from quillValue change:', { 
                                        name, 
                                        htmlValue: htmlValue.substring(0, 100),
                                        wasContent: currentContent.substring(0, 50),
                                        afterContent: afterUpdate.substring(0, 100),
                                        updateWorked: !isEmptyAfter
                                    });

                                    if (isEmptyAfter) {
                                        console.warn('Quill force update failed, trying direct HTML setting');
                                        editor.root.innerHTML = htmlValue;
                                    }
                                }, 50);
                            } catch (error) {
                                console.warn('Error force updating Quill:', error);
                            }
                        }
                    }
                }, 500);
                
                return () => clearTimeout(timeoutId);
            }
        }
    }, [quillValue, richText, name]);

    React.useEffect(() => {
        if (richText && quillRef.current && quillInitializedRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                const currentContent = editor.root.innerHTML || '';
                const htmlValue = quillValue || '';
                const prevValue = prevValueRef.current || '';

                const isEmptyContent = (content) => {
                    return !content || content === '' || content === '<p><br></p>' || content === '<p></p>';
                };
                
                const isCurrentEmpty = isEmptyContent(currentContent);
                const isNewEmpty = isEmptyContent(htmlValue);
                const isPrevEmpty = isEmptyContent(prevValue);

                if (!isInternalChangeRef.current && 
                    ((!isNewEmpty && isCurrentEmpty) ||
                     (isNewEmpty && !isCurrentEmpty) ||
                     (!isNewEmpty && !isCurrentEmpty && htmlValue !== currentContent && htmlValue !== prevValue))) {
                    const isFocused = document.activeElement === editor.root;

                    const shouldUpdate = !isFocused || 
                                       (!isNewEmpty && isCurrentEmpty) || 
                                       (isNewEmpty && !isCurrentEmpty);
                    
                    if (shouldUpdate) {
                        const updateQuill = () => {
                            try {
                                editor.clipboard.dangerouslyPasteHTML(htmlValue, 'silent');

                                const length = editor.getLength();
                                editor.setSelection(length - 1, 0, 'silent');
                                
                                prevValueRef.current = htmlValue;

                                setTimeout(() => {
                                    const afterUpdate = editor.root.innerHTML || '';
                                    const isEmptyAfter = isEmptyContent(afterUpdate);
                                    console.log('Quill content synced:', { 
                                        name, 
                                        originalValue: value?.substring(0, 50), 
                                        htmlValue: htmlValue.substring(0, 100), 
                                        wasEmpty: isCurrentEmpty,
                                        afterContent: afterUpdate.substring(0, 100),
                                        updateWorked: !isEmptyAfter
                                    });
                                    
                                    // If update didn't work, try again with direct HTML setting
                                    if (isEmptyAfter && !isEmptyContent(htmlValue)) {
                                        console.warn('Quill sync failed, trying direct HTML setting');
                                        editor.root.innerHTML = htmlValue;
                                    }
                                }, 50);
                            } catch (error) {
                                console.warn('Error updating Quill content:', error);
                            }
                        };
                        
                        // If Quill is ready, update immediately, otherwise wait a bit
                        if (editor.root && editor.root.innerHTML !== undefined) {
                            updateQuill();
                        } else {
                            setTimeout(updateQuill, 10);
                        }
                    }
                }

                isInternalChangeRef.current = false;
                prevValueRef.current = htmlValue;
            }
        }
    }, [quillValue, value, richText, name]);

    const handleChange = (content, delta, source, editor) => {
        isInternalChangeRef.current = true;
        change({
            target: {
                name,
                value: editor.getHTML() || '',
                rawText: editor.getText() || '',
                html: editor.getHTML() || '',
            }
        });
    };

    return (
        <div className="relative w-full mb-4 text-left">
            {label && (
                <label
                    htmlFor={name}
                    className="px-2 pt-1 text-xs absolute top-0 text-gray-500 bg-transparent z-10 w-full"
                >
                    {label} <sup className="text-red-500">{required ? '*' : null}</sup>
                </label>
            )}

            {richText ? (
                <div
                    id={toolbarId}
                    className="relative"
                    style={{ height: '100%', width: '100%'}}>
                    <ReactQuill
                        theme="snow"
                        modules={modules}
                        formats={formats}
                        ref={quillRef}
                        value={quillValue}
                        onChange={handleChange}
                        className={`mt-5 ${className}`}
                        placeholder={placeholder}
                        readOnly={disabled}
                        {...inputProps}
                    />

                    {showMathSymbolPicker && (
                        <div className="absolute bg-white border p-2 shadow" style={{zIndex: '999'}}>
                            {mathSymbols.map(symbol => (
                                <button
                                    key={symbol}
                                    className="p-1 hover:bg-gray-200"
                                    onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const range = quill.getSelection();
                                        if (range) {
                                            quill.insertText(range.index, symbol);
                                        }
                                    }}
                                >
                                    {symbol}
                                </button>
                            ))}
                        </div>
                    )}

                    {showSymbolPicker && (
                        <div className="absolute bg-white border p-2 shadow" style={{zIndex: '999'}}>
                            {symbolsValue.map(symbol => (
                                <button
                                    key={symbol}
                                    className="p-1 hover:bg-gray-200"
                                    onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const range = quill.getSelection();
                                        if (range) {
                                            quill.insertText(range.index, symbol);
                                        }
                                    }}
                                >
                                    {symbol}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <textarea
                    className={`px-2 text-base ${
                        label ? 'pt-5 pb-1' : 'pt-3 pb-3'
                    } block w-full mt-0 rounded-lg border border-gray-400 focus:outline-none focus:border-sky-500 focus:ring-0 ${className}`}
                    cols={cols}
                    disabled={disabled}
                    maxLength={maxlength}
                    name={name}
                    onChange={(e) =>
                        change({
                            target: {
                                name,
                                value: e.target.value,
                                rawText: e.target.value,
                                html: e.target.value
                            }
                        })
                    }
                    onBlur={onBlur}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    required={required}
                    rows={rows}
                    ref={textAreaRef}
                    value={value}
                    {...inputProps}
                    wrap="hard"
                />
            )}

            {helpText && (
                <small className="text-xs text-gray-500">
                    <em>{helpText}</em>
                </small>
            )}
        </div>
    );
};

TextAreaInput.propTypes = {
    change: PropTypes.func.isRequired,
    cols: PropTypes.number,
    disabled: PropTypes.bool,
    helpText: PropTypes.any,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    maxlength: PropTypes.number,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    rows: PropTypes.number,
    value: PropTypes.string.isRequired,
    inputRef: PropTypes.object,
    richText: PropTypes.bool
};

TextAreaInput.defaultProps = {
    disabled: false,
    helpText: null,
    label: '',
    placeholder: '',
    required: false,
    rows: 4,
    richText: false,
    onBlur: undefined,
    onFocus: undefined
};

export default TextAreaInput;
